import { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'components/UI/Modal/Modal';
import { removeCertificate } from 'services/TicketService';
import { Button } from 'components/UI/Button/Button';
import { Link } from 'components/UI/Link/Link';
import { ReactComponent as SeatIcon } from 'assets/icons/svgs/icon-approved.svg';
import { PaymentPageContext } from 'components/Payment/PaymentPageProvider';
import { toAudString } from 'utils/currency';
import './Prompts.scss';

export const RemoveCertificatePrompt = ({
  active,
  giftCertificate,
  stepName = 'Cart',
  onRequestClose,
  forceBackToCurrentPage,
  totalLineItem,
}) => {
  if (!giftCertificate) {
    return null;
  }

  const { GiftCertificateNumber, Amount } = giftCertificate;
  const [isBusy, setIsBusy] = useState(false);
  const [itemsRemoved, setItemsRemoved] = useState(false);
  const [error, setError] = useState(null);
  const { refreshOrderData } = useContext(PaymentPageContext);

  const closeModalHandler = () => {
    onRequestClose();
  };

  /**
   * Handle Delete Seats Requests
   */
  const handleRemoveConfirmed = async () => {
    // Show loading state
    setIsBusy(true);
    try {
      // Delete seats on server and capture new order
      const response = await removeCertificate(GiftCertificateNumber);
      setIsBusy(false);
      setItemsRemoved(true);
      if (refreshOrderData) {
        refreshOrderData(response.Order);
      }
    } catch (e) {
      console.error(e);
      setIsBusy(false);
      setError(e);
    }
  };

  const closeAndReload = () => {
    onRequestClose();
    /*
     * On close of modal, if this Prompt component does not have access to the function the 'refreshOrderData'
     * via the 'PaymentPageContext' (e.g. if it is not nested within the PaymentPageProvider) then just close
     * the modal and reload the page. This will allow the React components to be refresh with fresh Sitecore data.
     */
    if (!refreshOrderData) {
      window.location.reload();
    }
  };

  return (
    <Modal
      open={active}
      closeModalHandler={closeModalHandler}
      extendClass="red-box padding-30 cart-remove-prompt"
    >
      <div className="text-center">
        <p>
          Just checking - do you really want to remove the certificate for
          <br />
          <strong>{toAudString(Amount)}</strong> from your cart?
        </p>

        {!itemsRemoved && !error && (
          <Button
            onClick={handleRemoveConfirmed}
            disabled={isBusy}
            variant="primary"
            extendClass="btn-small"
            text={isBusy ? 'Removing...' : 'Yes, remove from my cart'}
          />
        )}

        {/* Success Banner */}
        {itemsRemoved && (
          <span className="btn action-complete">
            <SeatIcon className="prompt-icon" />
            Certificate removed from cart
          </span>
        )}

        <section>
          {!itemsRemoved && !error && (
            <Link
              href="#"
              variant="primary"
              onClick={closeModalHandler}
              extendClass="modal-link link-font-bold"
            >
              Cancel
            </Link>
          )}

          {/* Success Message with no more seats left for this event */}
          {itemsRemoved && !error && !forceBackToCurrentPage && (
            <Link href="#" onClick={closeModalHandler} extendClass="modal-link link-font-bold ">
              Cancel
            </Link>
          )}

          {/* Browse events */}
          {totalLineItem === 1 && itemsRemoved && !error && !forceBackToCurrentPage && (
            <div>
              <a href="/whats-on" className="modal-link link-font-bold">
                Browse Events
              </a>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div>
              <p>Certificate cannot be removed at this time</p>
              <Link href="#" onClick={closeModalHandler} extendClass="modal-link">
                Cancel
              </Link>
            </div>
          )}

          {((totalLineItem > 0 && itemsRemoved) || (itemsRemoved && forceBackToCurrentPage)) &&
            !error && (
              <p>
                <Link href="#" onClick={closeAndReload} extendClass="modal-link link-font-bold">
                  {`Return to ${stepName}`}
                </Link>
              </p>
            )}
        </section>
      </div>
    </Modal>
  );
};

RemoveCertificatePrompt.propTypes = {
  active: PropTypes.bool.isRequired,
  giftCertificate: PropTypes.object,
  stepName: PropTypes.string,
  onRequestClose: PropTypes.func.isRequired,
  forceBackToCurrentPage: PropTypes.bool,
  totalLineItem: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};
